"use client"
import { withMediaUrl } from "@/app/lib/basePath";
import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/app/ui/accordion";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import CTASection from "../home/CTA/CTASection";
import ProductShowcase from "./productshowcase";
import ProductsSideBySide from "./productsidebyside";
import ProductFeature from "./productfeature";
import GettingStartedSection from "../plotter/Started/GettingStartedSection";
import ProductComparison from "./productcomparison";
import ProductBenefits from "./productbenefits";
import ProductSteps from "./ProductSteps";
import React, { useEffect, useRef, useState } from "react";
import TestimonialsSection from "../home/Testimonials/TestimonialsSection";
import AIProviders from "../ui/AIProviders";
import ButtonGroup from "../ui/ButtonGroup";
import { defaultAIProvidersConfig } from "@/app/lib/config/aiProviders";
import { getLanguageFromPathname, withLanguagePrefix } from "@/app/lib/i18n-routing";


export default function ProductPageDetail(data: any) {
    const postData = data.data;
    const pathname = usePathname();
    const videoRef = useRef<HTMLVideoElement>(null);
    const faqs = data.data.content.filter((item: any) => item.type === "faq");
    const mediaType = typeof postData?.mediaType === "string"
        ? postData.mediaType.trim().toLowerCase()
        : "";
    const mediaValue = postData?.media ?? postData?.coverImage;
    const mediaAssets = (Array.isArray(mediaValue) ? mediaValue : [mediaValue])
        .filter((media: any) => Boolean(media?.url));
    const [mediaIndex, setMediaIndex] = useState(0);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const activeMedia = mediaAssets[mediaIndex] ?? mediaAssets[0];
    const mediaUrl = withMediaUrl(activeMedia?.url);
    const thumbnailValue = postData?.videoThumbnail;
    const thumbnail = Array.isArray(thumbnailValue) ? thumbnailValue[0] : thumbnailValue;
    const thumbnailUrl = withMediaUrl(thumbnail?.url);
    const embedUrl = postData?.embedUrl;
    const isImage = mediaType === "image" || (!mediaType && Boolean(mediaUrl));
    const isVideo = mediaType === "video";
    const isEmbed = mediaType.includes("embed");
    const hasBannerMedia = Boolean(mediaUrl || embedUrl);
    const bannerButtons = Array.isArray(postData?.button ?? postData?.buttons)
        ? postData.button ?? postData.buttons
        : postData?.button ?? postData?.buttons
            ? [postData.button ?? postData.buttons]
            : [];
    const title = postData.title ?? "";
    const highlight = postData.titleHighlight?.trim();
    const highlightIndex = highlight
        ? title.toLowerCase().indexOf(highlight.toLowerCase())
        : -1;

    useEffect(() => {
        setMediaIndex(0);
        setIsVideoReady(false);
        setShouldLoadVideo(false);
    }, [postData?.id, postData?.slug]);

    useEffect(() => {
        if (!isImage || mediaAssets.length < 2) return;
        const timer = window.setInterval(
            () => setMediaIndex((index) => (index + 1) % mediaAssets.length),
            5000,
        );
        return () => window.clearInterval(timer);
    }, [isImage, mediaAssets.length]);

    useEffect(() => {
        const video = videoRef.current;
        if (!isVideo || !mediaUrl || !video) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldLoadVideo(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );

        observer.observe(video);
        return () => observer.disconnect();
    }, [isVideo, mediaUrl]);

    useEffect(() => {
        if (!shouldLoadVideo) return;
        void videoRef.current?.play().catch(() => undefined);
    }, [shouldLoadVideo, mediaUrl]);
    // const product_showcase = data.data.content.filter((item: any) => item.type === "plotter_showcase_section");
    // const plotter_features = data.data.content.filter((item: any) => item.type === "plotter_features");
    // const plotter_benefits = data.data.content.filter((item: any) => item.type === "plotter_benefit");
    // const steps = data.data.content.filter((item: any) => item.type === "steps");
    // const comparison_table = data.data.content.filter((item: any) => item.type === "comparison_table");
    
    // console.log("faqs", faqs);
    // console.log("product_showcase", product_showcase);
    // console.log("plotter_features", plotter_features);
    // console.log("plotter_benefits", plotter_benefits);
    // console.log("steps", steps);
    // console.log("comparison_table", comparison_table);


    return (
        <>
            <section className="relative custom-minheight flex flex-col items-center justify-center">
                <div className="-translate-x-1/2 -z-1 absolute aspect-square left-1/2 max-w-full lg:max-w-[2560px] md:aspect-auto md:min-h-[1000px] pointer-events-none top-0 w-full">
                    <div className='shadow-[0_0_120px_110px_rgb(36,91,187),0_0_220px_218px_rgb(36,91,187),0_0_790px_320px_rgb(36,91,187)] h-[0.1px] w-[0.1px] left-1/3 top-1/4 -translate-middle absolute rounded-full backdrop-blur-xl bg-[rgb(22,154,156)] scale-150 opacity-40'></div>
                    <div className="absolute -bottom-40 left-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 right-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full lg:w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto relative z-10 pt-24 lg:pt-32 pb-4 md:pb-6 lg:pb-8">
                    <motion.div initial="hidden" animate="visible" variants={stagger}>
                        {/* <span className="inline-block px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-4">{postData.category.name}</span> */}
                        <motion.h1
                            variants={fadeUp}
                            className="banner-title max-w-4xl mx-auto text-center"
                        >
                            {highlightIndex >= 0 && highlight ? (
                                <>
                                    {title.slice(0, highlightIndex)}
                                    <span className="banner-highlight-title">
                                        {title.slice(highlightIndex, highlightIndex + highlight.length)}
                                    </span>
                                    {title.slice(highlightIndex + highlight.length)}
                                </>
                            ) : (
                                title
                            )}
                        </motion.h1>
                        <div className="flex items-center gap-3 mt-3 lg:mt-8 justify-center">
                            {/* <motion.div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border border-white/10 shrink-0">
                                <Image
                                    src="https://naavik.ai/favicon.svg"
                                    alt="Author"
                                    className="w-full h-full object-cover"
                                    title="Author"
                                    width={48}
                                    height={48}
                                    loading="lazy"
                                    fetchPriority="high"
                                    unoptimized
                                />
                            </motion.div> */}
                            <div>
                                {/* <motion.div className="text-white text-lg font-medium leading-none mb-2">
                                    {postData.author.name}
                                </motion.div> */}

                                <motion.div variants={fadeUp} className="flex items-center gap-2 banner-description !text-sm">
                                    <Calendar className="w-5 h-5" />
                                    {new Date(postData.publishAt).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </motion.div>
                            </div>

                        </div>

                    </motion.div>
                    {hasBannerMedia && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mt-8 lg:mt-16  max-w-4xl mx-auto"
                        >
                            <div className="relative">
                                <div aria-hidden className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-3xl blur-2xl w-full md:w-auto" />
                                {isImage && mediaUrl && (
                                    <motion.div className="relative bg-[#000c1e]/40 backdrop-blur-xl rounded-2xl flex items-center justify-center p-2 shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] border border-white/20">
                                        <Image key={mediaUrl} src={mediaUrl} alt={activeMedia?.alternativeText || "Product image"} title={activeMedia?.alternativeText || "Product image"} className="object-cover w-full h-full rounded-[10px]" width={512} height={378} decoding="async" loading="lazy" fetchPriority="high" />
                                        {mediaAssets.length > 1 && (
                                            <>
                                                <button type="button" onClick={() => setMediaIndex((index) => (index - 1 + mediaAssets.length) % mediaAssets.length)} aria-label="Show previous product image" className="absolute left-4 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/50 text-white transition hover:bg-black/70"><ChevronLeft className="h-5 w-5" /></button>
                                                <button type="button" onClick={() => setMediaIndex((index) => (index + 1) % mediaAssets.length)} aria-label="Show next product image" className="absolute right-4 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-black/50 text-white transition hover:bg-black/70"><ChevronRight className="h-5 w-5" /></button>
                                            </>
                                        )}
                                    </motion.div>
                                )}
                                {isVideo && mediaUrl && (
                                    <motion.div className="relative aspect-video bg-[#000c1e]/40 backdrop-blur-xl rounded-2xl flex items-center justify-center p-2 shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] border border-white/20">
                                        {thumbnailUrl && !isVideoReady && <Image src={thumbnailUrl} alt={thumbnail?.alternativeText || "Video thumbnail"} fill sizes="(max-width: 896px) 100vw, 896px" className="z-10 rounded-[10px] object-cover" />}
                                        <video ref={videoRef} className="h-full w-full rounded-[10px] object-cover" autoPlay={shouldLoadVideo} loop muted playsInline preload={shouldLoadVideo ? "auto" : "none"} poster={thumbnailUrl || undefined} onCanPlay={() => void videoRef.current?.play().catch(() => undefined)} onPlaying={() => setIsVideoReady(true)}>
                                            {shouldLoadVideo && <source src={mediaUrl} type={activeMedia?.mime || "video/mp4"} />}
                                        </video>
                                    </motion.div>
                                )}
                                {isEmbed && embedUrl && (
                                    <motion.div className="aspect-[9/9] md:aspect-[16/8] overflow-hidden rounded-[14px] bg-black/20 p-3 shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] backdrop-blur-[20px]"><iframe className="h-full w-full rounded-[10px] border-0" src={embedUrl} title={postData.title || "Live project"} /></motion.div>
                                )}
                            </div>
                            <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-3">
                                {isEmbed && bannerButtons.length > 0 && (
                                    <div className="w-full min-w-0 md:w-auto">
                                        <ButtonGroup
                                            buttons={bannerButtons}
                                            containerClass="flex flex-wrap items-center gap-2"
                                            btnClassPrimary="btn-primary !px-4 !py-2 !text-sm whitespace-nowrap"
                                            btnClassSecondary="btn-secondary !px-4 !py-2 !text-sm whitespace-nowrap"
                                        />
                                    </div>
                                )}
                                <div className="w-full min-w-0 overflow-hidden md:flex-1 md:flex md:justify-end">
                                    <AIProviders
                                        data={postData?.ai_configuration || defaultAIProvidersConfig}
                                        variant="inline"
                                        size="sm"
                                        showTitle={true}
                                        showLabelsOnMobile={false}
                                        className="w-full justify-start md:justify-end"
                                        providersClassName="flex-wrap gap-1 sm:gap-2"
                                    />
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>

            <section className="pb-4 md:pb-6 lg:pb-8 overflow-hidden">
                <div className="container mx-auto">
                    {/* <div className="post-content mx-auto max-w-4xl "> */}
                        
                        {postData?.content?.map((item: any, index: number) => (
                            <React.Fragment key={`${item.type}-${index}`}>
                            <div className="post-content mx-auto max-w-4xl ">
                                <div key={`${item.type}-${index}`}>
                                {item.type === "text" && (
                                    <div
                                        className="prose prose-invert max-w-none"
                                        dangerouslySetInnerHTML={{
                                            __html: item.html,
                                        }}
                                    />
                                )}
                            </div>
                            </div>
                            <div key={`${item.type}-${index}`}>
                                {item.type === "plotter_showcase_section" && (
                                    <ProductShowcase data={item} />
                                )}
                                {item.type === "plotter_benefit" && (
                                    <ProductBenefits data={item} />
                                )}
                                {item.type === "plotter_showcase_section_side_by_side" && (
                                    <ProductsSideBySide data={item} />
                                )}
                                {item.type === "plotter_features" && (
                                    <ProductFeature data={item} />
                                )}
                                {item.type === "steps" && (
                                    <ProductSteps data={item} />
                                )}
                                {item.type === "comparison_table" && (
                                    <ProductComparison data={item} />
                                )}
                                {item.type === "cta" && (
                                    <div className="my-8 md:my-10">
                                        <div className="mx-auto flex max-w-4xl items-center gap-3 md:gap-4">
                                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                                            <ButtonGroup
                                                buttons={[
                                                    {
                                                        title: item.title || item.label || item.text || "Learn More",
                                                        url: (() => {
                                                            const rawUrl = item.url || item.href || item.path || "#";
                                                            return rawUrl && rawUrl !== "#"
                                                                ? withLanguagePrefix(rawUrl, getLanguageFromPathname(pathname))
                                                                : "#";
                                                        })(),
                                                        target: item.target || "_self",
                                                        variant: item.variant || "primary",
                                                        content: (
                                                            <>
                                                                <span>{item.title || item.label || item.text || "Learn More"}</span>
                                                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                                            </>
                                                        ),
                                                    },
                                                ]}
                                                containerClass="flex justify-center"
                                                btnClassPrimary="btn-primary group"
                                                btnClassSecondary="btn-secondary group"
                                            />
                                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                                        </div>
                                    </div>
                                )}
                                {item.type === "image" && (
                                    <figure className="space-y-3">

                                        <div className="relative overflow-hidden rounded-2xl z-[1]">

                                            <Image
                                                src={withMediaUrl(item.imageUrl)}
                                                alt={item.imageAlt || "Blog Image"}
                                                title={item.imageAlt || "Blog Image"}
                                                className="object-cover h-full w-full"
                                                width={900}
                                                height={574}
                                                loading="lazy"
                                                fetchPriority="high"
                                            />

                                        </div>

                                        {item.caption && (
                                            <figcaption className="text-center text-sm text-white/60">
                                                {item.caption}
                                            </figcaption>
                                        )}

                                    </figure>
                                )}
                            </div>
                            </React.Fragment>
                        ))}
                         {faqs.length > 0 && (
                             <>
                            <h2 className="section-title mb-1 lg:mb-4 text-center mt-12">
                                Frequently Asked Questions
                            </h2>
                            <section className="relative py-6 lg:py-8">
                                <div aria-hidden className="absolute inset-0 star-field pointer-events-none" />
                                <div className="container mx-auto max-w-4xl relative z-10">
                                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                                        <Accordion type="single" collapsible className="space-y-3">
                                            {faqs.map((faq: any, i: number) => (
                                                <motion.div key={i} variants={fadeUp}>
                                                    <AccordionItem
                                                        value={`item-${i}`}
                                                        className="group rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_4px_24px_-12px_rgba(0,0,0,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/[0.06] hover:border-white/20 hover:shadow-[0_8px_32px_-12px_hsl(var(--primary)/0.25)] data-[state=open]:bg-white/[0.07] data-[state=open]:border-white/25 px-5"
                                                    >
                                                        <AccordionTrigger className="hover:no-underline text-left font-semibold py-3.5 leading-normal ">{faq.question}</AccordionTrigger>
                                                        <AccordionContent className="text-muted-foreground leading-relaxed pb-5 -translate-y-1">
                                                            <div
                                                                className="post-content mx-auto max-w-4xl "
                                                                dangerouslySetInnerHTML={{ __html: faq.answerHtml }}
                                                            />
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                </motion.div>
                                            ))}
                                        </Accordion>
                                    </motion.div>
                                </div>
                            </section>
                            </>
                        )}
                        <section className="py-4 md:py-6 lg:py-8 !px-0">
                        <TestimonialsSection data={postData.testimonial_section} />
                        </section>

                    {/* </div> */}
                </div>
            </section>

            <section className="py-6 lg:py-8 relative z-[1]">
                <div className="container mx-auto">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                        className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-8 mx-auto max-w-6xl"
                    >

                        {postData.recommendedBlogs?.map((post: any) => (
                            <motion.div
                                key={post.id}
                                variants={fadeUp}
                                className="flex flex-col"
                            >
                                <Link
                                    href={`/blogs/${post.slug}`}
                                    title={post.title}
                                    className="flex flex-col group flex-grow"
                                >

                                    <div className="overflow-hidden transition-all hover:-translate-y-1 bg-white/[0.04] backdrop-blur-[8px] border border-white/10 shadow-xl lg:border-r lg:border-white/10 rounded-lg flex-grow flex flex-col">

                                        <div className="aspect-video relative">

                                            <Image
                                                className="object-cover h-full w-full"
                                                src={withMediaUrl(post.coverImage?.url)}
                                                alt={post.title}
                                                title={post.title}
                                                width={360}
                                                height={202}
                                                loading="lazy"
                                                fetchPriority="high"
                                            />

                                            <span className="px-3 py-1.5 rounded-full text-xs border border-teal-400/30 text-white transition-all duration-[250ms] ease-out bg-teal-700/60 hover:border-teal-400/50 backdrop-blur-[8px] absolute top-2 right-2">
                                                {post.category.name}
                                            </span>

                                        </div>

                                        <div className="overflow-hidden group p-6 flex flex-col flex-grow">

                                            <h3 className="text-lg font-semibold group-hover:text-secondary transition-colors">
                                                {post.title}
                                            </h3>

                                            <p className="mt-2 text-white/70 text-sm leading-[1.7]">
                                                {post.excerpt}
                                            </p>

                                            <div className="flex items-center gap-3 justify-between mt-auto">

                                                <div className="mt-4 flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all">
                                                    Read More
                                                    <ArrowRight className="w-4 h-4" />
                                                </div>

                                                <span className="text-xs text-white/70 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {new Date(post.publishAt).toLocaleDateString("en-GB", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    })}

                                                </span>

                                            </div>

                                        </div>

                                    </div>

                                </Link>
                            </motion.div>
                        ))}

                    </motion.div>

                </div>
            </section>

            <CTASection data={postData.transform_section} />
        </>
    );
}
